import React, { useRef } from 'react';
import cn from 'classnames';
import { Spinner } from './primitives';
import { MERGE_FIELDS } from './constants';
import { formatPhoneInput } from './helpers';

// The schedule inputs are interpreted in the admin's browser timezone (moment(...).utc() in
// NoirBroadcast converts from local → UTC), so label them with the actual local tz, not a fixed "ET".
const LOCAL_TZ_LABEL = (() => {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value || 'local time';
  } catch {
    return 'local time';
  }
})();

const MessageComposer = ({
  cohort,
  title,
  setTitle,
  msg,
  setMsg,
  curatorFilterActive,
  brandFilterActive,
  mode,
  setMode,
  scheduleDate,
  setScheduleDate,
  scheduleTime,
  setScheduleTime,
  sendState,
  setSendState,
  onSend,
  testPhone,
  setTestPhone,
  testState,
  setTestState,
  testError,
  setTestError,
  onSendTest
}) => {
  const textareaRef = useRef(null);

  const insertMergeField = f => {
    const ta = textareaRef.current;
    if (!ta) return;
    const s = ta.selectionStart;
    const e = ta.selectionEnd;
    setMsg(msg.slice(0, s) + f + msg.slice(e));
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(s + f.length, s + f.length);
    }, 0);
  };

  // {{curator_name}} / {{brand_name}} are only offered when the cohort is filtered by curator /
  // brand — they resolve per recipient at send time (their top curator / most-purchased brand).
  const mergeFields = [
    ...MERGE_FIELDS,
    ...(curatorFilterActive ? [['{{curator_name}}', 'Curator Name']] : []),
    ...(brandFilterActive ? [['{{brand_name}}', 'Brand Name']] : [])
  ];

  const previewMsg = msg
    .replace(/\{\{first_name\}\}/g, 'Sarah')
    .replace(/\{\{tier_name\}\}/g, 'Gold')
    .replace(/\{\{curator_name\}\}/g, 'Alex')
    .replace(/\{\{brand_name\}\}/g, 'Nordstrom');
  const charCount = msg.length;
  const charWarning = charCount > 160 ? 'over' : charCount > 140 ? 'amber' : null;
  const canSend = cohort.withPhone > 0 && msg.trim() && sendState === 'idle';

  return (
    <div className='nb-card nb-composer-card'>
      <span className='nb-composer-title'>COMPOSE MESSAGE</span>

      <input
        className='nb-broadcast-title-input'
        type='text'
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder='Broadcast title (for your reference in the log)'
        maxLength={255}
      />

      <div className='nb-merge-fields'>
        {mergeFields.map(([f, l]) => (
          <button key={f} onClick={() => insertMergeField(f)}>
            + {l}
          </button>
        ))}
        <button className='disabled' title='Coming soon'>
          + Recent Heart · soon
        </button>
      </div>

      <div className='nb-textarea-wrap'>
        <textarea
          ref={textareaRef}
          value={msg}
          onChange={e => setMsg(e.target.value)}
          placeholder='Type your message here…'
          className={charWarning || ''}
        />
        <span className={cn('nb-char-count', charWarning)}>{charCount} / 160</span>
      </div>
      {charWarning === 'over' && (
        <p className='nb-over-warning'>Messages over 160 characters are sent as 2 SMS segments</p>
      )}

      {msg.trim() && (
        <div className='nb-preview-box'>
          <span className='nb-preview-label'>Preview: </span>
          <em>{previewMsg}</em>
        </div>
      )}

      <div className='nb-test-box'>
        <div className='nb-test-header'>
          <span className='nb-test-title'>SEND A TEST TEXT</span>
          <span className='nb-test-sub'>— preview before broadcasting</span>
        </div>
        <div className='nb-test-input-row'>
          <div className='nb-phone-wrap'>
            <span className='nb-phone-prefix'>+1</span>
            <input
              type='tel'
              value={testPhone}
              onChange={e => {
                setTestPhone(formatPhoneInput(e.target.value));
                setTestError('');
                setTestState('idle');
              }}
              placeholder='(555) 867-5309'
              className={testError ? 'error' : ''}
            />
          </div>
          <button
            onClick={onSendTest}
            disabled={!msg.trim() || testState === 'sending'}
            className={cn({ sent: testState === 'sent', sending: testState === 'sending' })}
          >
            {testState === 'sending' ? (
              <>
                <Spinner /> Sending…
              </>
            ) : testState === 'sent' ? (
              '✓ Sent'
            ) : (
              'Send test'
            )}
          </button>
        </div>
        {testError && <p className='nb-test-error'>{testError}</p>}
        {testState === 'sent' && (
          <p className='nb-test-confirm'>Test sent to +1 {testPhone}. Dynamic fields rendered with sample values.</p>
        )}
        <p className='nb-test-foot'>
          Dynamic fields (first name, tier) will render as sample values in the test. The test is only sent to this number — not to the cohort.
        </p>
      </div>

      <div className='nb-mode-toggle'>
        {['now', 'schedule'].map(m => (
          <button key={m} className={cn({ active: mode === m })} onClick={() => setMode(m)}>
            {m === 'now' ? 'Send Now' : 'Schedule'}
          </button>
        ))}
      </div>

      {mode === 'schedule' && (
        <div className='nb-schedule-inputs'>
          <input type='date' value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} />
          <input type='time' value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} />
          <span>{LOCAL_TZ_LABEL}</span>
        </div>
      )}

      {sendState === 'confirming' && (
        <div className='nb-confirm-bar'>
          <span className='nb-confirm-text'>
            Send to {cohort.withPhone} shoppers {mode === 'now' ? 'now' : 'as scheduled'}? This cannot be undone.
          </span>
          <div className='nb-confirm-actions'>
            <button className='nb-cancel' onClick={() => setSendState('idle')}>
              Cancel
            </button>
            <button className='nb-confirm-go' onClick={onSend}>
              {mode === 'schedule' ? 'Schedule Text' : 'Confirm Send'}
            </button>
          </div>
        </div>
      )}

      {sendState === 'sent' && (
        <div className='nb-sent-banner'>
          ✓ Broadcast {mode === 'schedule' ? 'scheduled' : 'sent'} to {cohort.withPhone} shoppers
        </div>
      )}

      <button className={cn('nb-send-cta', { sending: sendState === 'sending' })} onClick={onSend} disabled={!canSend}>
        {sendState === 'sending' ? (
          <>
            <Spinner color='#fff' /> {mode === 'schedule' ? 'Scheduling…' : 'Sending…'}
          </>
        ) : mode === 'schedule' ? (
          `Schedule for ${cohort.withPhone} Shoppers`
        ) : (
          `Send to ${cohort.withPhone} Shoppers`
        )}
      </button>
    </div>
  );
};

export default MessageComposer;
